import { t } from 'i18next';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import ComponentCircleLoader from '../../components/Loaders/ComponentCircleLoader';
import ReactSelectDropdown from '../../components/ReactSelectDropdown';
import { filterOption } from '../../components/ReactSelectDropdown/ReactSelectDropdown';
import BarTooltip from '../../components/Tooltip/BarTooltip';
import CommonTooltip from '../../components/Tooltip/CommonTooltip';
import {
  CommonFlex,
  CommonText,
  Container,
  FilterLabel,
  InputWithLabelSection,
  PageTitle,
} from '../../styledComponents/common';
import {
  AbsoluteScaleContainer,
  Bar,
  CenteredText,
  HorizontalScale,
  ScrollableContainer,
  TooltipWrapper,
  VerticalScale,
} from './itemCountingDashboardStyles';

const ItemsCountingDashboard = ({
  officesShortList,
  itemsList,
  getOfficesShortList,
  getItemsList,
  itemCountingDashboardData,
  getItemCountingDashboardData,
  isLoading,
  publicZoneList,
  getPublicZoneList,
}) => {
  const [max, setMax] = useState(5000); // Initialize with default value
  const [selectedOffice, setSelectedOffice] = useState({
    label: t('all_offices'),
    value: null,
  });
  const [selectedItem, setSelectedItem] = useState({
    label: t('all_items'),
    value: null,
  });
  const [selectedPublicZone, setSelectedPublicZone] = useState(null);

  const [officeOptions, setOfficeOptions] = useState([
    { label: t('all_offices'), value: null },
  ]);
  const [itemOptions, setItemOptions] = useState([
    { label: t('all_items'), value: null },
  ]);
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);

  const [transformedData, setTransformedData] = useState([]);

  const [tooltipData, setTooltipData] = useState({
    title: '',
    text: '',
    x: 0,
    y: 0,
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = (event, item, type) => {
    const rect = event.target.getBoundingClientRect();
    let text = '';
    if (type === 'received') {
      text = `${t('counted_quantity')} : ${item.received_quantity}`;
    } else if (type === 'allotted') {
      text = `${t('allotted_quantity')} : ${item.allotted_quantity}`;
    } else if (type === 'current') {
      text = `${t('current_quantity')} : ${item.current_quantity}`;
    }
    const title = item.item_name ? item.item_name : '';

    // Calculate tooltip position
    const tooltipWidth = 200; // Approximate width of the tooltip
    const x = Math.max(
      Math.min(
        rect.left + rect.width / 2,
        window.innerWidth - tooltipWidth - 30, // Ensure it doesn't overflow the right edge
      ),
      10, // Ensure it doesn't overflow the left edge
    );
    const y = rect.top - 10; // Position above the bar

    setTooltipData({ title, text, x, y });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipData({ title: '', text: '', x: 0, y: 0 });
  };

  const handleOfficeChange = selectedOption => {
    setSelectedOffice(selectedOption);
    selectedOption?.value &&
      setSelectedItem({
        label: t('all_items'),
        value: null,
      });
  };

  const handlePublicZoneChange = selectedOption => {
    setSelectedPublicZone(selectedOption);
    // Reset office when public zone changes
    setSelectedOffice({
      label: t('all_offices'),
      value: null,
    });
  };

  useEffect(() => {
    getOfficesShortList();
    getItemsList();
    getPublicZoneList && getPublicZoneList();
  }, []);

  // Auto-select "Mina" public zone if available, otherwise select first zone
  useEffect(() => {
    if (publicZoneList?.length && !selectedPublicZone) {
      // Try to find "Mina" in the public zone list
      const minaZone = publicZoneList.find(
        zone => zone.name.toLowerCase() === 'mina',
      );

      // If Mina is found, select it; otherwise select the first zone
      if (minaZone) {
        setSelectedPublicZone({
          label: minaZone.name,
          value: minaZone.id,
        });
      } else {
        const firstZone = publicZoneList[0];
        setSelectedPublicZone({
          label: firstZone.name,
          value: firstZone.id,
        });
      }
    }
  }, [publicZoneList, selectedPublicZone]);

  useEffect(() => {
    // Get offices based on selected public zone
    if (selectedPublicZone?.value) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    } else {
      getOfficesShortList();
    }
  }, [selectedPublicZone]);

  useEffect(() => {
    const payload = {};
    if (selectedOffice?.value) {
      payload.office_id = selectedOffice.value;
    }
    if (selectedItem?.value) {
      payload.item_id = selectedItem.value;
    }
    if (selectedPublicZone?.value) {
      payload.public_zone_id = selectedPublicZone.value;
    }
    if (payload.public_zone_id) {
      getItemCountingDashboardData(payload);
    }
  }, [selectedOffice, selectedItem, selectedPublicZone]);

  // Transform the new data format to match the expected format
  useEffect(() => {
    if (itemCountingDashboardData && Array.isArray(itemCountingDashboardData)) {
      const transformedItems = [];
      let maxRequired = 0;

      itemCountingDashboardData.forEach(office => {
        office.items.forEach(item => {
          // Calculate the maximum of all relevant values
          maxRequired = Math.max(
            maxRequired,
            item.required_quantity,
            item.current_quantity,
            item.counted_quantity,
          );

          transformedItems.push({
            item_id: item.item_id,
            office_id: office.office_id,
            allotted_quantity: item.required_quantity,
            current_quantity: item.current_quantity,
            received_quantity: item.counted_quantity,
            office_name: office.office_name,
            item_name: item.item_name, // add item name for tooltip
          });
        });
      });

      setMax(Math.ceil((maxRequired > 0 ? maxRequired : 5000) / 10) * 10);
      setTransformedData(transformedItems);
    }
  }, [itemCountingDashboardData]);

  useEffect(() => {
    if (officesShortList?.length) {
      const options = [
        { label: t('all_offices'), value: null },
        ...officesShortList.map(office => ({
          label: office.name,
          value: office.id,
        })),
      ];
      setOfficeOptions(options);
    }
  }, [officesShortList]);

  useEffect(() => {
    if (itemsList?.length) {
      const itemsArray = itemsList.map(item => ({
        label: item.name,
        value: item.id,
      }));

      const options = selectedOffice?.value
        ? [{ label: t('all_items'), value: null }, ...itemsArray]
        : itemsArray;

      setItemOptions(options);
    }
  }, [itemsList, selectedOffice]);

  useEffect(() => {
    if (
      !selectedOffice?.value &&
      itemOptions?.length &&
      selectedItem?.value === null
    ) {
      const item = itemOptions.find(item => {
        return item.value;
      });
      if (item) {
        setSelectedItem(item);
      }
    }
  }, [selectedOffice, itemOptions]);

  useEffect(() => {
    if (publicZoneList?.length) {
      const options = publicZoneList.map(zone => ({
        label: zone.name,
        value: zone.id,
      }));
      setPublicZoneOptions(options);
    }
  }, [publicZoneList]);

  return (
    <div className="content">
      <PageTitle>{t('items_counting_dashboard')}</PageTitle>
      <Container padding="30px">
        {/* Render tooltip using a portal */}
        {showTooltip &&
          ReactDOM.createPortal(
            <BarTooltip
              title={tooltipData.title}
              tooltipText={tooltipData.text}
              left={`${tooltipData.x}px`}
              top={`${tooltipData.y}px`}
              bgColor="#1f2939"
              textColor="#fff"
              padding="7px 15px"
              border="1px solid #1f2939"
              fontSize="12px"
              width="auto"
              height="auto"
            />,
            document.body,
          )}
        <CommonText name title fontSize="20px">
          {t('items_counting_dashboard')}
        </CommonText>
        <CommonFlex gap="20px" margin="20px 0">
          <InputWithLabelSection width="200px">
            <FilterLabel>{t('public_zone')}</FilterLabel>
            <ReactSelectDropdown
              isSearchable={false}
              value={selectedPublicZone}
              options={publicZoneOptions}
              placeholder={t('select_public_zone')}
              onChange={handlePublicZoneChange}
              styles={CreateProjectDropdown()}
              filterOption={filterOption}
            />
          </InputWithLabelSection>{' '}
          <InputWithLabelSection width="200px">
            <FilterLabel>{t('office')}</FilterLabel>
            <ReactSelectDropdown
              isSearchable={false}
              value={selectedOffice}
              options={officeOptions}
              placeholder={t('select_office')}
              onChange={handleOfficeChange}
              styles={CreateProjectDropdown()}
              filterOption={filterOption}
            />
          </InputWithLabelSection>
          <InputWithLabelSection width="200px">
            <FilterLabel>{t('item')}</FilterLabel>
            <ReactSelectDropdown
              isSearchable={false}
              value={selectedItem}
              options={itemOptions}
              placeholder={t('select_item')}
              onChange={setSelectedItem}
              styles={CreateProjectDropdown()}
              filterOption={filterOption}
            />
          </InputWithLabelSection>
        </CommonFlex>

        {isLoading ? (
          <ComponentCircleLoader />
        ) : (
          <ScrollableContainer>
            <CommonFlex
              gap="20px"
              alignItems="flex-start"
              justifyContent="start"
              style={{ height: '100%' }}
            >
              <CommonFlex direction="column" gap="20px" alignItems="flex-start">
                {Array.from({ length: 11 }, (_, index) => {
                  const v = (10 - index) * (max / 10);
                  return <VerticalScale key={v}>{v}</VerticalScale>;
                })}
              </CommonFlex>
              <CommonFlex
                style={{
                  height: 'calc(100% - 24px)',
                  marginTop: '11px',
                  zIndex: 2,
                  position: 'relative',
                  width: '100%',
                  padding: '0',
                }}
                justifyContent="flex-start"
              >
                {transformedData.map(item => (
                  <CommonFlex
                    direction="column"
                    alignItems="bottom"
                    justifyContent="flex-end"
                    key={`${item.office_id}-${item.item_id}`} // Use combined key
                    style={{ height: '100%', position: 'relative' }}
                    gap="0"
                  >
                    <CommonText
                      name
                      title
                      fontSize="12px"
                      style={{
                        position: 'absolute',
                        bottom: '-20px',
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      {item.office_name}
                    </CommonText>
                    <CommonFlex
                      gap="0"
                      style={{ height: '100%' }}
                      alignItems="end"
                    >
                      <Bar
                        bgColor="#196B24"
                        height={`${(100 * item.received_quantity) / max}%`}
                        onMouseEnter={e =>
                          handleMouseEnter(e, item, 'received')
                        }
                        onMouseLeave={handleMouseLeave}
                      />
                      <Bar
                        bgColor="#E97132"
                        height={`${(100 * item.allotted_quantity) / max}%`}
                        onMouseEnter={e =>
                          handleMouseEnter(e, item, 'allotted')
                        }
                        onMouseLeave={handleMouseLeave}
                      />
                      <Bar
                        bgColor="#156082"
                        height={`${(100 * item.current_quantity) / max}%`}
                        onMouseEnter={e => handleMouseEnter(e, item, 'current')}
                        onMouseLeave={handleMouseLeave}
                      />
                    </CommonFlex>
                  </CommonFlex>
                ))}
              </CommonFlex>
            </CommonFlex>
            <AbsoluteScaleContainer direction="column" gap="20px">
              {Array.from({ length: 11 }, (_, index) => {
                const v = (10 - index) * (max / 10);
                return (
                  <HorizontalScale
                    key={v}
                    width={
                      transformedData?.length > 0
                        ? `${Math.max(80 * transformedData.length, 700)}px`
                        : '100%'
                    }
                  />
                );
              })}
            </AbsoluteScaleContainer>
          </ScrollableContainer>
        )}
        <CenteredText>{t('offices')}</CenteredText>
      </Container>
    </div>
  );
};

export default ItemsCountingDashboard;
